import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Edit, Share2 } from "lucide-react";
import type { Division } from "@/lib/types/deck-builder";
import { Deck } from "@/lib/classes/deck";
import type { UnitMap } from "@/lib/types/deck-builder";
import { useDataStore } from "@/lib/store/data-store";
import { EditDeck } from "@/components/DeckBuilder/EditDeck";
import { SummaryView } from "@/components/DeckBuilder/SummaryView";
import { DeckPreview } from "@/components/DeckDrafter/DeckPreview";
import { DivisionGroupedPicker } from "@/components/DivisionGroupedPicker";
import { groupDivisionsForSelect } from "@/lib/utils/division-select-groups";
import { copyDeckShareUrl } from "@/lib/utils/deck-library";
import { toast } from "sonner";

export function DeckBuilder() {
  const { divisionId } = useParams<{ divisionId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { units, divisions, loadingUnits, loadingDivisions } = useDataStore();
  const [selectedDivision, setSelectedDivision] = useState<Division | null>(null);
  const [deck, setDeck] = useState<Deck | null>(null);
  const [unitMap, setUnitMap] = useState<UnitMap>({});
  const [displayMode, setDisplayMode] = useState(false);
  const [editingDeckId, setEditingDeckId] = useState<number | null>(null);
  const isResettingRef = useRef(false);

  useEffect(() => {
    if (units) {
      const map: UnitMap = {};
      for (const unit of units) {
        map[unit.descriptorName] = unit;
      }
      setUnitMap(map);
    }
  }, [units]);

  // Handle deck code import (must run before division selection to prevent clearing)
  useEffect(() => {
    const deckCode = searchParams.get("code");
    const deckIdParam = searchParams.get("deckId");
    const editMode = searchParams.get("edit") === "true";
    
    // Skip if we already have a deck loaded (prevents re-import when code is preserved in URL)
    if (deck && deckCode) {
      const currentDeckCode = deck.toDeckCode();
      // If the current deck code matches the URL code, don't re-import
      if (currentDeckCode === deckCode) {
        return;
      }
    }
    
    if (deckCode && divisions && divisions.length > 0 && Object.keys(unitMap).length > 0) {
      try {
        console.log("Attempting to import deck code:", deckCode.substring(0, 50) + "...");
        const importedDeck = Deck.fromDeckCode(deckCode, { unitMap, divisions });
        console.log("Deck imported successfully:", {
          division: importedDeck.division.name,
          divisionId: importedDeck.division.id,
          unitCount: importedDeck.units.length,
        });
        setDeck(importedDeck);
        setSelectedDivision(importedDeck.division);
        
        // Store deck ID if in edit mode
        if (editMode && deckIdParam) {
          const deckId = parseInt(deckIdParam, 10);
          if (!isNaN(deckId)) {
            setEditingDeckId(deckId);
          }
        }
        
        // If code is present but edit is NOT true, automatically show preview/display mode
        // This allows sharing decks via URL without entering edit mode
        if (!editMode) {
          setDisplayMode(true);
          // Preserve code parameter in URL for sharing purposes
          const newParams = new URLSearchParams();
          newParams.set("code", deckCode);
          navigate(`/deck-builder/${importedDeck.division.descriptor}?${newParams.toString()}`, { replace: true });
        } else {
          // Navigate without the code parameter to prevent re-import, but preserve edit params
          const newParams = new URLSearchParams();
          if (deckIdParam) {
            newParams.set("deckId", deckIdParam);
          }
          newParams.set("edit", "true");
          const queryString = newParams.toString();
          navigate(`/deck-builder/${importedDeck.division.descriptor}${queryString ? `?${queryString}` : ""}`, { replace: true });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        console.error("Failed to import deck code:", error);
        console.error("Error details:", {
          message: errorMessage,
          deckCodeLength: deckCode.length,
          divisionsCount: divisions.length,
          unitsCount: Object.keys(unitMap).length,
        });
        // Redirect to import page with error message
        navigate(`/deck-import?error=${encodeURIComponent(`Failed to import deck code: ${errorMessage}`)}`, { replace: true });
      }
    }
  }, [searchParams, divisions, unitMap, navigate]);

  // Handle division selection from URL (only if no deck code and no existing deck)
  useEffect(() => {
    const deckCode = searchParams.get("code");
    // Skip division selection if:
    // 1. There's a deck code in the URL (let deck code import handle it)
    // 2. We already have a deck (don't overwrite it)
    // 3. We're intentionally resetting (prevent interference with resetDivision)
    if (deckCode || deck || isResettingRef.current) {
      // Clear the reset flag after checking
      if (isResettingRef.current && !divisionId) {
        isResettingRef.current = false;
      }
      return;
    }
    
    // Only auto-select division from URL if we have a divisionId and no explicit reset
    // If we're at /deck-builder (no divisionId), don't try to select anything
    if (divisionId && divisions && divisions.length > 0 && Object.keys(unitMap).length > 0) {
      const division = divisions.find((d) => d.descriptor === divisionId || d.id.toString() === divisionId);
      if (division) {
        selectDivision(division);
      }
    }
  }, [divisionId, divisions, unitMap, searchParams, deck]);

  const selectDivision = (division: Division) => {
    if (Object.keys(unitMap).length === 0) {
      console.error("Unit map not loaded");
      return;
    }
    setSelectedDivision(division);
    const newDeck = new Deck({ division, unitMap });
    setDeck(newDeck);
    navigate(`/deck-builder/${division.descriptor}`, { replace: true });
  };

  const resetDivision = () => {
    // Set flag to prevent useEffect from interfering
    isResettingRef.current = true;
    // Clear state
    setSelectedDivision(null);
    setDeck(null);
    setDisplayMode(false);
    // Navigate to base route without any divisionId parameter
    navigate("/deck-builder", { replace: true });
  };

  const divisionGroups = useMemo(
    () => (divisions ? groupDivisionsForSelect(divisions) : []),
    [divisions]
  );

  if (loadingDivisions || loadingUnits) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (deck && selectedDivision) {
    if (displayMode) {
      const handleEditInBuilder = () => {
        const deckCode = deck.toDeckCode();
        const newParams = new URLSearchParams();
        newParams.set("code", deckCode);
        if (editingDeckId) {
          newParams.set("deckId", editingDeckId.toString());
        }
        newParams.set("edit", "true");
        navigate(`/deck-builder/${selectedDivision.descriptor}?${newParams.toString()}`, { replace: true });
        setDisplayMode(false);
      };

      const handleCopyShareLink = async () => {
        try {
          const deckCode = deck.toDeckCode();
          await copyDeckShareUrl(deckCode);
          toast.success("Share link copied to clipboard");
        } catch (error) {
          console.error("Failed to copy share link:", error);
          toast.error("Failed to copy share link");
        }
      };

      return (
        <div className="h-full overflow-y-auto">
          <div className="container mx-auto px-4 py-8 max-w-7xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">{selectedDivision.name}</h2>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={handleCopyShareLink} 
                  variant="outline"
                  size="sm"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Copy Share Link
                </Button>
                <Button onClick={handleEditInBuilder} variant="default" size="sm">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit in Builder
                </Button>
              </div>
            </div>
            <Card className="mb-6">
              <CardContent className="p-4">
                <SummaryView deck={deck} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <h3 className="text-lg font-semibold mb-4">Deck Preview</h3>
                <div className="overflow-x-auto">
                  <DeckPreview deck={deck} />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
        <EditDeck
          deck={deck}
          divisions={divisions || []}
          unitMap={unitMap}
          deckId={editingDeckId || undefined}
          onSummaryClick={() => setDisplayMode(true)}
          onChangeDivision={resetDivision}
          onDeckCleared={() => {
            deck.clearDeck();
            setDeck(new Deck({ division: selectedDivision, unitMap }));
          }}
        />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="container mx-auto px-4 py-4 max-w-xl">
        <h1 className="text-3xl font-bold mb-2">Select a Division</h1>
        <p className="text-sm text-muted-foreground mb-4">
          Choose a division to open the deck builder. You can filter by name, country, or coalition.
        </p>
        <DivisionGroupedPicker
          divisionGroups={divisionGroups}
          value=""
          selectedDivision={undefined}
          divisionHref={(d) => `/deck-builder/${encodeURIComponent(d)}`}
          onValueChange={(descriptor) => {
            const division = divisions?.find((d) => d.descriptor === descriptor);
            if (division) selectDivision(division);
          }}
          triggerClassName="max-w-md"
        />
      </div>
    </div>
  );
}

